import styled, { css } from 'styled-components'
import { flatButton } from './shared.styles'
import { darkOrange, orange, blue, black, mutedGrey, white, grey, border1, darkGrey } from '@/theme/colors'
import { radii, media } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { Button } from '@components/Button'

const phone = `${media.phone}`

/**
 * Range fills alternate by destination index — peach for even, blue-grey for
 * odd — so adjacent destinations read as separate stretches. Indexed by
 * `calendarDayState().colorIndex`.
 */
export const rangeFill = (index: number): string =>
	index % 2 === 0
		? `color-mix(in srgb, ${orange} 28%, transparent)`
		: `color-mix(in srgb, ${blue} 25%, transparent)`

/** A destination's range colour at full strength — what its tint is mixed from — for a day picked out of the range. */
export const rangeAccent = (index: number): string => (index % 2 === 0 ? orange : blue)

/** The neutral fill of a trip day no destination owns (the edit flow's trailing legacy days). */
export const uncoveredFill = `color-mix(in srgb, ${darkGrey} 55%, transparent)`

/** What a day cell is painted with: a destination's range tint, the neutral uncovered tint, or nothing. */
export type DayFill = number | 'uncovered' | null

const fillOf = (fill: Exclude<DayFill, null>): string =>
	fill === 'uncovered' ? uncoveredFill : rangeFill(fill)

/** Fills the step's phone-width column, which keeps the square day cells tappable. */
export const CalendarCard = styled.div`
	margin-top: 16px;
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.card};
	padding: 14px 16px 18px;

	${phone} {
		padding: 12px 10px 14px;
	}
`

export const CalendarHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

export const MonthNavButton = styled(Button).attrs({ variant: 'icon' as const })`
	color: ${darkOrange};
`

export const MonthTitle = styled.div`
	${text.label}
	color: ${black};
`

export const WeekdayRow = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 4px;
	margin-top: 10px;
`

export const WeekdayLetter = styled.span`
	text-align: center;
	padding-bottom: 4px;
	${text.caption}
	color: ${mutedGrey};
`

export const DayGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(7, 1fr);
	gap: 4px;
`

export const BlankCell = styled.span`
	aspect-ratio: 1;
`

export const DayCell = styled.button<{ $fill: DayFill }>`
	${flatButton}
	aspect-ratio: 1;
	border-radius: ${radii.small};
	${text.bodyXs}
	color: ${black};

	&:hover {
		background: ${grey};
	}

	${({ $fill }) =>
		$fill !== null &&
		css`
			background: ${fillOf($fill)};

			&:hover {
				background: ${fillOf($fill)};
				filter: brightness(0.95);
			}
		`}
`
